import { useEffect, useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { supabaseSync } from '@/lib/supabase-sync';
import type { User, Session } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  loading: boolean;
  session: Session | null;
  isInitialized: boolean;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setSession: (session: Session | null) => void;
  setInitialized: (initialized: boolean) => void;
}

const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  loading: true,
  isInitialized: false,
  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ loading }),
  setSession: (session) => set({ session }),
  setInitialized: (initialized) => set({ isInitialized: initialized }),
}));

// Prevent duplicate profile creation
const profileCreationInProgress = new Set<string>();

async function ensureUserProfile(user: User) {
  if (profileCreationInProgress.has(user.id)) return;
  profileCreationInProgress.add(user.id);
  try {
    const { data } = await supabase
      .from('users')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    if (!data) {
      await supabase.from('users').insert({
        id: user.id,
        email: user.email!,
        name: user.user_metadata?.name || user.user_metadata?.full_name || user.email?.split('@')[0],
        avatar_url: user.user_metadata?.avatar_url,
      });
    }
  } catch (err) {
    console.error('ensureUserProfile error:', err);
  } finally {
    profileCreationInProgress.delete(user.id);
  }
}

export const useAuth = () => {
  const { user, loading, session, isInitialized, setUser, setLoading, setSession, setInitialized } = useAuthStore();

  useEffect(() => {
    let isMounted = true;

    // getSession() reads from local storage — no network call, no timeout needed
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        supabaseSync.setUser(session.user);
        ensureUserProfile(session.user);
      }
      setLoading(false);
      setInitialized(true);
    }).catch((err) => {
      console.error('getSession error:', err);
      if (isMounted) {
        setLoading(false);
        setInitialized(true);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      setSession(session);
      setUser(session?.user ?? null);

      if (event === 'SIGNED_IN' && session?.user) {
        supabaseSync.setUser(session.user);
        ensureUserProfile(session.user);
      }

      if (event === 'SIGNED_OUT') {
        supabaseSync.setUser(null);
        localStorage.removeItem('kwachalite-storage');
      }

      // Mark initialized on first auth event if not already
      setInitialized(true);
      setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (error) throw error;
    return data;
  }, []);

  const loginWithEmail = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }, []);

  const signUpWithEmail = useCallback(async (email: string, password: string, fullName?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/email-verification`,
        data: { full_name: fullName },
      },
    });
    if (error) throw error;
    return data;
  }, []);

  const logout = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setSession(null);
  }, [setUser, setSession]);

  return useMemo(() => ({
    user, loading, session, isInitialized,
    login, loginWithEmail, signUpWithEmail, logout,
  }), [user, loading, session, isInitialized, login, loginWithEmail, signUpWithEmail, logout]);
};
